// src/pages/Menu/Vinculation/panel/MailDraftsContext.jsx
import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { getMailTemplates } from "../../../../services/membershipmailsService";

/**
 * Estructura de un draft
 * { subject: string, body: string }
 */
const MailDraftsContext = createContext(null);

export function MailDraftsProvider({ app, initialAccept, initialReject, initialAcceptance, initialReceived, children }) {
  const [acceptDraft, setAcceptDraft] = useState(initialAccept || { subject: "", body: "" });
  const [rejectDraft, setRejectDraft] = useState(initialReject || { subject: "", body: "" });
  const [acceptanceDraft, setAcceptanceDraft] = useState(initialAcceptance || { subject: "", body: "" });
  const [receivedDraft, setReceivedDraft] = useState(initialReceived || { subject: "", body: "" });
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  // Cargar plantillas desde la API al montar el componente
  useEffect(() => {
    let mounted = true;
    
    const loadTemplates = async () => {
      try {
        const templates = await getMailTemplates();
        if (!mounted) return;

        // Actualizar con las plantillas guardadas, o usar las por defecto si no existen
        if (templates.ACCEPTED) {
          setAcceptDraft(templates.ACCEPTED);
        } else if (initialAccept) {
          setAcceptDraft(initialAccept);
        }

        if (templates.REJECTED) {
          setRejectDraft(templates.REJECTED);
        } else if (initialReject) {
          setRejectDraft(initialReject);
        }

        if (templates.ACCEPTANCE) {
          setAcceptanceDraft(templates.ACCEPTANCE);
        } else if (initialAcceptance) {
          setAcceptanceDraft(initialAcceptance);
        }

        if (templates.RECEIVED) {
          setReceivedDraft(templates.RECEIVED);
        } else if (initialReceived) {
          setReceivedDraft(initialReceived);
        }

        setTemplatesLoaded(true);
      } catch (error) {
        console.error('Error al cargar plantillas desde la API:', error);
        // En caso de error, usar los valores iniciales
        if (mounted) {
          if (initialAccept) setAcceptDraft(initialAccept);
          if (initialReject) setRejectDraft(initialReject);
          if (initialAcceptance) setAcceptanceDraft(initialAcceptance);
          if (initialReceived) setReceivedDraft(initialReceived);
          setTemplatesLoaded(true);
        }
      }
    };

    loadTemplates();

    return () => {
      mounted = false;
    };
  }, []); // Solo al montar

  // Si cambia el app (selección), reseteamos a los iniciales que nos pasen
  // PERO solo si ya cargamos las plantillas y no hay valores guardados
  useEffect(() => {
    if (!templatesLoaded) return;
    // Solo resetear si no hay valores guardados en la API (ya se cargaron arriba)
    // Este efecto es principalmente para compatibilidad con el comportamiento anterior
  }, [app?.id, templatesLoaded]); // eslint-disable-line

  const value = useMemo(() => ({
    app,
    acceptDraft, setAcceptDraft,
    rejectDraft, setRejectDraft,
    acceptanceDraft, setAcceptanceDraft,
    receivedDraft, setReceivedDraft,
  }), [app, acceptDraft, rejectDraft, acceptanceDraft, receivedDraft]);

  return <MailDraftsContext.Provider value={value}>{children}</MailDraftsContext.Provider>;
}

export function useMailDrafts() {
  const ctx = useContext(MailDraftsContext);
  if (!ctx) throw new Error("useMailDrafts debe usarse dentro de <MailDraftsProvider>");
  return ctx;
}

// Helper para obtener el draft inicial de aceptación final
export function getInitialAcceptance() {
  return { subject: "", body: "" };
}
